import React, { useState, useEffect } from "react";
import TopNav from "./layout/TopNav";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import { isTokenExpired, getUserId, persistUser } from "../utils/persist";
import LogoutPage from "./pages/LogoutPage";
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import GlossaryPage from "./pages/GlossaryPage";
import AccountPage from "./pages/AccountPage";
import PageNotFound from "./pages/PageNotFound";
import PostPage from "./pages/PostPage";
import { makeRequest } from "../utils/request";
import TermsOfUse from "./pages/TermsOfUse";
import "./assets/dragAndDropTouch";
import InteractionDemoPage from "./pages/InteractionDemoPage";
import LogService from "../utils/logs";
import GoodbyePage from "./pages/GoodbyePage";
import TableOfContents from "./pages/TableOfContents/TableOfContents";
import moment from "moment";
import { useLocation } from "react-router";
import { AppContext } from "./AppContext";
import { clearUserData } from "../utils/persist";
import Sharedposts from "./pages/Sharedposts";

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!isTokenExpired());
  const [freeUntil, setFreeUntil] = useState(null);
  const [hasAccess, setHasAccess] = useState(null); // null is state for loading access
  const [hasAccessUntil, setHasAccessUntil] = useState(null);
  const [gracePeriod, setGracePeriod] = useState(0);
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [accessUpdated, setAccessUpdated] = useState(false);
  const userId = getUserId();
  const [isMember, setIsMember] = useState(false);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [plans, setPlans] = useState([]);
  const [spxPurchase, setSpxPurchase] = useState(null);
  const history = useHistory();
  const [sharedRedirect, setSharedRedirect] = useState(null);
  const [userPermission, setUserPermissions] = useState({});

  let isFreePeriod = moment(freeUntil).isAfter(moment());
  if (query.get("nonFree")) {
    isFreePeriod = false;
  }

    window.onLogin = async (data) => {
    await persistUser(data);
    setIsAuthenticated(true);
  };

  useEffect(() => {
    setPlansLoading(true);
    Promise.all([
      makeRequest("get", "/api/userpermission/getuserpermissions"),
    ]).then((res) => {
      if (query.get("member")) {
        setIsMember(true);
      } else {
        const isMemberFlag = res[0]?.isMember?.isMember || false;
        setIsMember(isMemberFlag);
      }
      setPlans(res[0].plans);
      setPlansLoading(false);
      const access = res[0].hasAccess || [];
      const myPlan = res[0].myPlan || [];
      setHasAccess(access.hasAccess);
      setHasAccessUntil(access.hasAccessUntil || null);
      setFreeUntil(access.freeUntil);
      setGracePeriod(access.gracePeriod);
      setSpxPurchase(access.enableSpxPurchase); // API response {true || false}
      setMyPlan(myPlan);
      setLoading(false);
      setAccessUpdated(true);
      setUserPermissions(res[0]);
      const queryPath = new URLSearchParams(location.search);
        const queryRedirect = queryPath.get("redirect");

        if (queryRedirect && isAuthenticated) {
            history.push(queryRedirect);
        }
    });
  }, [isAuthenticated]);

  useEffect(() => {
      if (!isAuthenticated) {
          const redirectUri = query.get("redirect");
          //hack to be fixed with new app router update
          if (location.pathname.includes('login') && redirectUri) {
              //do nothing
          } else {
              const sharedUrl =
                  location.pathname +
                  (location.hash ? location.hash : "") +
                  (location.search ? location.search : "");

              if (sharedUrl !== "/") {
                  setSharedRedirect(sharedUrl);
              }
              if (
                  !isAuthenticated &&
                  sharedRedirect !== "/" &&
                  sharedRedirect !== null &&
                  sharedRedirect !== "/login" &&
                  sharedRedirect !== "/logout" &&
                  sharedRedirect !== "/terms" &&
                  sharedRedirect !== "/resources" &&
                  sharedRedirect !== "/SharedPosts/Show/:contentKey/:id" &&
                  sharedRedirect !== "/interaction-demo" &&
                  sharedRedirect.indexOf("/?redirect=") === -1
              ) {
                  history.push({
                      search: `?redirect=${sharedRedirect}`,
                  });
          }
      }
    }
  }, [location.pathname, hasAccess]);

  const onSubscribe = async (plan) => {
    const access = await makeRequest("get", "/api/benefits/hasAccess");
    setHasAccess(access.hasAccess);
    setHasAccessUntil(access.hasAccessUntil || null);
    setFreeUntil(access.freeUntil);
    setGracePeriod(access.gracePeriod);
    setMyPlan(plan);
  };

  if (loading) return <div></div>;

  return (
    <AppContext.Provider value={{ isFirstRender, setIsFirstRender }}>
      <div>
        <TopNav isAuthenticated={isAuthenticated} hasAccessUntil={hasAccessUntil} hasAccess={hasAccess} isMember={isMember} />
        <div>
          <Switch>
            <Route path="/" exact>
              <LandingPage
                isAuthenticated={isAuthenticated}
                onSubscribe={onSubscribe}
                freeUntil={freeUntil}
                myPlan={myPlan}
                hasAccess={hasAccess}
                hasAccessUntil={hasAccessUntil}
                gracePeriod={gracePeriod}
                isFreePeriod={isFreePeriod}
                isMember={isMember}
                plans={plans}
                plansLoading={plansLoading}
                spxPurchase={spxPurchase}
                userId={userId}
              />
            </Route>

            <Route path="/login" exact>
              <LoginPage
                setAccessUpdated={setAccessUpdated}
                accessUpdated={accessUpdated}
                onSuccess={async () => {
                  await LogService.logUserSignin();
                  setIsAuthenticated(true);
                }}
              />
            </Route>
            <Route path="/logout" exact>
              <LogoutPage
                onSuccess={async () => {
                  await LogService.logUserSignout();
                  setIsAuthenticated(false);
                  clearUserData();
                  history.push("/");
                }}
              />
            </Route>

            <Route path="/terms" exact>
              <TermsOfUse isAuthenticated={isAuthenticated} userPermission={userPermission} userId={userId} />
            </Route>
            <Route
              path="/SharedPosts/Show/:contentKey/:id"
              exact
              render={(props) => (
                <Sharedposts {...props} isAuthenticated={isAuthenticated} isMember={isMember} hasAccess={hasAccess} hasAccessUntil={hasAccessUntil} userPermission={userPermission} userId={userId} />
              )}
            />
            <Route path="/interaction-demo" exact>
              <InteractionDemoPage />
            </Route>

            <Route
              exact
              path="/home"
              render={() =>
                hasAccess ? (
                  <HomePage
                    isAuthenticated={isAuthenticated}
                    myPlan={myPlan}
                    freeUntil={freeUntil}
                    hasAccessUntil={hasAccessUntil}
                    userId={userId}
                    gracePeriod={gracePeriod}
                    userPermission={userPermission}
                    isMember={isMember}
                    hasAccess={hasAccess}
                  />
                ) : (
                  <Redirect to="/" />
                )
              }
            />

            <Route
              exact
              path="/glossary"
              render={() =>
                hasAccess ? (
                  <GlossaryPage isAuthenticated={isAuthenticated} hasAccess={hasAccess} hasAccessUntil={hasAccessUntil} isMember={isMember} userPermission={userPermission} userId={userId} />
                ) : (
                  <Redirect to="/" />
                )
              }
            />
            <Route
              exact
              path="/tableofcontent"
              render={() =>
                hasAccess ? (
                  <TableOfContents isAuthenticated={isAuthenticated} hasAccess={hasAccess} hasAccessUntil={hasAccessUntil} isMember={isMember} userPermission={userPermission} userId={userId} />
                ) : (
                  <Redirect to="/" />
                )
              }
            />
            <Route
              exact
              path="/account"
              render={() =>
                hasAccess ? (
                  <AccountPage
                    isAuthenticated={isAuthenticated}
                    freeUntil={freeUntil}
                    myPlan={myPlan}
                    hasAccessUntil={hasAccessUntil}
                    gracePeriod={gracePeriod}
                    isMember={isMember}
                    userPermission={userPermission}
                    userId={userId}
                    hasAccess={hasAccess}
                  />
                ) : (
                  <Redirect to="/" />
                )
              }
            />

            <Route
              exact
              path="/cancellation-feedback"
              render={() => (hasAccess ? <GoodbyePage /> : <Redirect to="/" />)}
            />

            <Route
              exact
              path="/posts/:contentKey/:id"
              render={(props) => (
                <PostPage {...props} isAuthenticated={isAuthenticated} isMember={isMember} hasAccess={hasAccess} hasAccessUntil={hasAccessUntil} userPermission={userPermission} userId={userId} />
              )}
            />

            <Route exact path={"/404"} render={() => <PageNotFound />} />

            <Route>
              <Redirect to="/404" />
            </Route>

            <Route
              render={({ staticContext }) => {
                if (staticContext) {
                  staticContext.statusCode = 404;
                }
                return <PageNotFound />;
              }}
            />
          </Switch>
        </div>
      </div>
    </AppContext.Provider>
  );
};

export default App;
